/**
 * Point-at-it notes for the preview stage — the store half of the /artifact-agent "agentation"
 * experience, ported (see `AnnotateLayer` for the surface and `withAnnotations` for the chat side).
 *
 * A module store on the `stageSignals` pattern (subscribe / version / serverVersion), because the two
 * readers live in different trees: the layer draws markers from it, and the chat host reads it when a
 * message goes out. Handing the set through props would thread it across the whole route for two
 * consumers; the source page did exactly that (`onPayload` up, `annotateScope` down) and most of its
 * measured bugs were that plumbing going stale across slot switches.
 *
 * SESSION-LOCAL, DELIBERATELY. The source SDK persisted notes in `localStorage` keyed by pathname,
 * and the whole scoping layer around it (`seedSdkStore`/`mirrorSdkStore`, the frozen `scopeRef`)
 * exists to fight what that caused: artifact 1's notes rendering on artifact 2 at stale coordinates.
 * Notes here are consumed by the next chat message and never written to the blob or to storage — a
 * reload starts clean, which is what a note that says "make THIS bigger" deserves, because "this"
 * does not survive a regeneration anyway.
 *
 * COORDINATE SPACE: the artifact's own 1920x1080 (`ARTIFACT_W/H`) — the space `ArtifactDocument`'s
 * children are authored in and the space the generator writes absolute positions in. The source SDK
 * stored viewport-relative numbers (x as % of page width, y in page px) because its notes lived on
 * the page; ours live on the slide, so the slide's units are the honest ones and they survive any
 * zoom. `x`/`y` are the note's anchor point; `w`/`h` present means an AREA note ("Area selection" in
 * the SDK's vocabulary — its drag-select affordance) and then `x`/`y` are the rect's top-left.
 */

export interface SlideAnnotation {
  /** Stable within the session — markers key on it, edit/remove address it. */
  id: string;
  /** Anchor point in artifact px (rect top-left when `w`/`h` are present). */
  x: number;
  y: number;
  /** Present = area note (the SDK's drag-select). Absent = point note (its click-an-element). */
  w?: number;
  h?: number;
  /** The note itself — the "What should change?" answer. */
  text: string;
  /** Human name of what the note points at (hit-tested off the baked shadow root when reachable). */
  element?: string;
  /** Readable artifact-real DOM path (`section > .stats > h2`) — never carries host segments. */
  elementPath?: string;
  /** The `data-eid` of the hand-edited element the note points at, when it has one. */
  eid?: string;
}

/** What callers hand to `addAnnotation` — the id is minted here so it cannot collide. */
export type NewSlideAnnotation = Omit<SlideAnnotation, 'id'>;

const listeners = new Set<() => void>();
let version = 0;

function bump(): void {
  version += 1;
  for (const listener of listeners) listener();
}

/** `useSyncExternalStore`'s subscribe half. */
export function subscribeAnnotations(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** A counter, not the data: every reader re-runs `listAnnotations` for the slides it cares about. */
export function annotationsVersion(): number {
  return version;
}

/** The server renders once and never sees a note, so its snapshot has to be a constant. */
export function annotationsServerVersion(): number {
  return 0;
}

/* ── the notes themselves ──────────────────────────────────────────────────────────────────────── */

/**
 * Keyed by `AnnotationKey`, not an artifact number.
 *
 * A deck artifact is identified by a number and a kit composition by a role string ("cover"), and the bus
 * assuming the former is the only reason annotation could not be mounted on the kit — the same shape of
 * block that kept the morph loader off that surface. Widened rather than migrated: every numeric caller
 * is unchanged, because a number is still a key.
 */
export type AnnotationKey = number | string;

const bySlide = new Map<AnnotationKey, readonly SlideAnnotation[]>();

/**
 * Which note is lit, across the whole session.
 *
 * ONE, not one per slide, and that is the point: the thing being lit is a pairing between a chip in the
 * composer and a mark on the artifact, and only one pointer exists. Keeping it here rather than in either
 * component is the same argument the notes themselves make — the two ends live in different trees (the
 * agent column and the preview stage) and threading it between them would mean lifting it to the route,
 * which is the plumbing this module exists to avoid.
 *
 * The key rides along because the reader is the LAYER, which draws one artifact: without it, hovering
 * note 2 on artifact 7 would light note 2 on whatever artifact happened to be open. It is an
 * `AnnotationKey` for the same reason the map is — a kit composition is named, not numbered.
 */
let focused: { slideId: AnnotationKey; id: string } | null = null;
/** Stable empty result — `listAnnotations` must return the SAME reference while nothing changed,
 *  or a `useSyncExternalStore` reader re-rendering on it would loop. */
const EMPTY: readonly SlideAnnotation[] = [];
let nextId = 0;

/** The slide's notes, in the order they were added — the order the markers are numbered in. */
export function listAnnotations(slideId: AnnotationKey): readonly SlideAnnotation[] {
  return bySlide.get(slideId) ?? EMPTY;
}

/** Mint and store a note; returns it (the layer opens nothing on it — the composer already ran). */
export function addAnnotation(slideId: AnnotationKey, note: NewSlideAnnotation): SlideAnnotation {
  nextId += 1;
  const added: SlideAnnotation = { ...note, id: `note-${nextId}` };
  bySlide.set(slideId, [...listAnnotations(slideId), added]);
  bump();
  return added;
}

/** Patch one note in place (edit popup's Save). Unknown ids are a no-op, not an error — the note
 *  may have been cleared by a send while the popup was open. */
export function updateAnnotation(slideId: AnnotationKey, id: string, patch: Partial<NewSlideAnnotation>): void {
  const notes = listAnnotations(slideId);
  const index = notes.findIndex((note) => note.id === id);
  if (index === -1) return;
  const next = notes.slice();
  next[index] = { ...notes[index], ...patch };
  bySlide.set(slideId, next);
  bump();
}

/** The edit popup's trash, and the delete-hover click. */
export function removeAnnotation(slideId: AnnotationKey, id: string): void {
  const notes = listAnnotations(slideId);
  const next = notes.filter((note) => note.id !== id);
  if (next.length === notes.length) return;
  bySlide.set(slideId, next.length === 0 ? EMPTY : next);
  // A light on a note that no longer exists would keep one chip raised forever, because the leave that
  // would have cleared it belongs to an element that has just unmounted.
  if (focused?.slideId === slideId && focused.id === id) focused = null;
  bump();
}

/** Called by the host after a send consumes the staged notes — the source's `clearNotes` action:
 *  sent notes must never ride into the next message. */
export function clearAnnotations(slideId: AnnotationKey): void {
  if (listAnnotations(slideId).length === 0) return;
  bySlide.set(slideId, EMPTY);
  // Whatever was lit is gone with them. Left set, the next set of notes would open with a stale id
  // matching nothing, or worse, matching a reused one.
  if (focused?.slideId === slideId) focused = null;
  bump();
}

/* ── which note is lit ─────────────────────────────────────────────────────────────────────────── */

/**
 * Light one note, or clear the light. Called from BOTH ends — the chip's pointer enter/leave, and the
 * marker's — so the cross-highlight is symmetric without either side knowing the other exists.
 *
 * Passing `null` clears unconditionally; passing an id clears only if it is the one currently lit, so a
 * leave event arriving after another note has already been entered cannot blank the new one. That
 * ordering is not hypothetical: pointer-leave on the chip you are leaving fires after pointer-enter on
 * the chip you are entering whenever they are adjacent, which in a tray they always are.
 */
export function focusAnnotation(slideId: AnnotationKey, id: string | null): void {
  const next = id === null ? null : { slideId, id };
  if (next === null && focused !== null && focused.slideId !== slideId) return;
  if (focused?.slideId === next?.slideId && focused?.id === next?.id) return;
  focused = next;
  bump();
}

/** The lit note, or null. Readers re-run this on every version bump. */
export function focusedAnnotation(): { slideId: AnnotationKey; id: string } | null {
  return focused;
}

/* ── reopening a note from the other end ───────────────────────────────────────────────────────── */

/**
 * A request to open one note's editor, made from OUTSIDE the layer.
 *
 * Pressing a chip in the composer has to reopen the note it stands for, and the popup that edits a note
 * is drawn by the layer — in the preview pane, in a different tree. There is no prop path between them,
 * for the same reason the notes themselves live here.
 *
 * A NONCE rather than a flag, because the same note may be asked for twice in a row: press a chip,
 * cancel the popup, press the same chip again. With a plain id the second press would set state that
 * already equalled itself and the layer would never hear about it.
 */
let editRequest: { slideId: AnnotationKey; id: string; nonce: number } | null = null;

/** Ask the layer to open this note. Consumed by whichever layer is drawing that slide. */
export function requestAnnotationEdit(slideId: AnnotationKey, id: string): void {
  editRequest = { slideId, id, nonce: (editRequest?.nonce ?? 0) + 1 };
  // Lighting it too, so the mark answers on the same frame the popup opens on rather than only once
  // the pointer arrives — the user pressed a chip in another pane and has to be shown where it went.
  focused = { slideId, id };
  bump();
}

/** The outstanding request, or null. Readers compare the nonce to the last one they acted on. */
export function annotationEditRequest(): { slideId: AnnotationKey; id: string; nonce: number } | null {
  return editRequest;
}

/** The lit note's id for ONE slide — what both the layer and the tray actually ask. */
export function focusedAnnotationId(slideId: AnnotationKey): string | null {
  return focused?.slideId === slideId ? focused.id : null;
}
